import { useParams, Link, useNavigate } from 'react-router-dom'
import { Plus, FileText, Edit3, Trash2, Eye, EyeOff, BookOpen, LayoutList, BarChart2, Lock } from 'lucide-react'
import { Button } from '@brix/ui'
import { useAuthStore } from '@/store/authStore'
import { usePermissions } from '@/hooks/usePermissions'
import {
  useBlogPosts,
  useDeleteBlogPost, useToggleBlogPostPublished,
} from '@/hooks/useBlogPosts'

export function BlogAdminPage() {
  const { siteId = '' } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { isAuthorOnly } = usePermissions()

  const { data: allPosts = [], isLoading } = useBlogPosts(siteId)
  const posts = isAuthorOnly ? allPosts.filter((p) => p.authorId === user?.id) : allPosts
  const deleteMutation = useDeleteBlogPost(siteId)
  const togglePublish = useToggleBlogPostPublished(siteId)

  return (
    <div className="p-8">
      {/* Breadcrumb + sub-nav */}
      <div className="mb-6">
        <Link to="/sites" className="text-sm text-gray-400 hover:text-violet-600">← Sites</Link>
        <div className="mt-2 flex items-center gap-1 border-b border-gray-200 pb-0">
          <Link
            to={`/sites/${siteId}/pages`}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-900 border-b-2 border-transparent hover:border-gray-300 -mb-px transition-colors"
          >
            <LayoutList className="h-4 w-4" /> Pages
          </Link>
          <span className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-violet-700 border-b-2 border-violet-600 -mb-px">
            <BookOpen className="h-4 w-4" /> Blog posts
          </span>
          <Link
            to={`/sites/${siteId}/analytics`}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-900 border-b-2 border-transparent hover:border-gray-300 -mb-px transition-colors"
          >
            <BarChart2 className="h-4 w-4" /> Analytics
          </Link>
        </div>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Blog posts</h1>
          {isAuthorOnly && (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-amber-600">
              <Lock className="h-3 w-3" />
              You can only see and edit your own posts
            </p>
          )}
        </div>
        <Button onClick={() => navigate(`/sites/${siteId}/blog/new`)}>
          <Plus className="h-4 w-4" />
          New post
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-violet-600 border-t-transparent" />
        </div>
      ) : posts.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 py-20">
          <FileText className="mb-3 h-10 w-10 text-gray-300" />
          <p className="text-sm text-gray-500">No posts yet</p>
          <Button className="mt-4" onClick={() => navigate(`/sites/${siteId}/blog/new`)}>
            <Plus className="h-4 w-4" /> Write your first post
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {posts.map((post) => (
            <div key={post.id} className="flex items-start gap-4 rounded-xl border border-gray-200 bg-white px-5 py-4">
              {post.coverImage ? (
                <img src={post.coverImage} alt="" className="h-14 w-20 rounded-lg object-cover shrink-0" />
              ) : (
                <div className="h-14 w-20 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                  <FileText className="h-5 w-5 text-gray-300" />
                </div>
              )}

              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{post.title}</p>
                {post.excerpt && (
                  <p className="mt-0.5 text-sm text-gray-400 line-clamp-1">{post.excerpt}</p>
                )}
                <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                  {post.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">{tag}</span>
                  ))}
                  {post.publishedAt && (
                    <span className="text-xs text-gray-400">
                      {new Date(post.publishedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                </div>
              </div>

              <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${
                post.published ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
              }`}>
                {post.published ? 'Published' : 'Draft'}
              </span>

              <div className="flex gap-1 shrink-0">
                <button
                  onClick={() => navigate(`/sites/${siteId}/blog/${post.id}/edit`)}
                  className="p-2 rounded-lg text-gray-400 hover:text-violet-600 hover:bg-violet-50 transition-colors cursor-pointer"
                  title="Edit"
                >
                  <Edit3 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => togglePublish.mutate({ id: post.id, publish: !post.published })}
                  className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
                  title={post.published ? 'Unpublish' : 'Publish'}
                >
                  {post.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => { if (confirm(`Delete "${post.title}"?`)) deleteMutation.mutate(post.id) }}
                  className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
